import { createClient } from '@supabase/supabase-js'
import type { Database } from './database.types'

/**
 * Production Supabase project — public values, safe to embed in the
 * client bundle. Used as a fallback when the build-time VITE_*
 * environment variables aren't set (e.g. CI builds where the .env
 * file isn't checked in). Both the URL and anon JWT are designed
 * to be public:
 *   - Project URL is in every API call
 *   - Anon key is the public client identity; RLS does the actual
 *     security on every query
 *
 * Without this fallback, every Xcode Cloud build that doesn't have
 * the env vars correctly piped through ships with empty values and
 * the app gets stuck on "Supabase isn't configured yet" forever.
 *
 * If we ever rotate the anon key or move projects, update both
 * places (the .env for local dev + this fallback). Local dev still
 * reads from .env, so nothing changes for developer workflow.
 */
const PROD_URL = 'https://kppibkvegzfvkiecxulu.supabase.co'
const PROD_ANON_KEY =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImtwcGlia3ZlZ3pmdmtpZWN4dWx1Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzYyNzE3NDksImV4cCI6MjA5MTg0Nzc0OX0.kxvePtM8OUxHedzVHA3VzHauXfypXPsLTE_m_OZv1qw'

const envUrl = import.meta.env.VITE_SUPABASE_URL
const envKey = import.meta.env.VITE_SUPABASE_ANON_KEY

const url = envUrl || PROD_URL
const key = envKey || PROD_ANON_KEY

// Always considered configured now — the fallback IS the production
// config. Kept as a function so any callers who used `configured` to
// gate UI keep working.
const configured = Boolean(url && key)

if (!envUrl || !envKey) {
   
  console.warn(
    '[supabase] VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY not set in build env. ' +
    'Falling back to production project values baked into the bundle.'
  )
}

/**
 * Singleton Supabase client. Typed against the row shapes in
 * database.types.ts — keep that file in sync with supabase/migrations/.
 */
export const supabase = createClient<Database>(url, key, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
})

/** Tiny helper — is the client actually configured? Always true now
 *  thanks to the production-values fallback. */
export function supabaseConfigured(): boolean {
  return configured
}
